import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";
import { PLIS_TO_MM_LABEL, PLIS_RECONNUS } from "../utils/empilementPopulations";

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const COLORS = {
  carac: "#C0392B",
  moy: "#378ADD",
  bar: "#3b5f7a",
  barEdge: "#1e3a50",
  yellow: "#E6FF3E",
  gray: "#94a3b8",
  red: "#f87171",
};

const CONF_KEYS = [
  { id: "all", label: "Tous" },
  { id: "CONFORME", label: "Conforme" },
  { id: "NON CONFORME", label: "Non conforme" },
];

const POP_KEYS = [
  { id: "all", label: "Toutes" },
  ...PLIS_RECONNUS.map((n) => ({ id: String(n), label: PLIS_TO_MM_LABEL[n] })),
  { id: "autre", label: "Autre" },
];

// EN 14358 (loi normale continue) — identique à l'outil masse_vol.py
function ksContinu(n) {
  return (6.5 * n + 6) / (3.7 * n - 3);
}

function statsNormale(xj) {
  const n = xj.length;
  if (n < 2) return null;
  const moy = xj.reduce((a, b) => a + b, 0) / n;
  const variance = xj.reduce((a, b) => a + (b - moy) * (b - moy), 0) / (n - 1);
  const s = Math.sqrt(variance);
  const plancher = 0.05 * moy;
  const sRetenu = Math.max(s, plancher);
  const ks = ksContinu(n);
  const valeurCarac = moy - ks * sRetenu;
  return { n, moyenne: moy, s, plancher, sRetenu, ks, valeurCarac };
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color, fontSize: 12 }}>
          {p.name}: {p.value ?? "N/A"}
        </div>
      ))}
    </div>
  );
}

export default function DashboardMasseVolumiqueSection({ active = true }) {
  const { t, ui } = useI18n();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [records, setRecords] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const [conf, setConf] = useState("all");
  const [pop, setPop] = useState("all");

  useEffect(() => {
    if (!active || loaded) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch("/api/dashboard/masse-volumique");
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.status === "error") throw new Error(json.message || "Erreur API");
        if (!cancelled) {
          setRecords(json.records || []);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Erreur chargement masse volumique");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [active, loaded]);

  const filtered = useMemo(() => {
    if (!records) return [];
    return records.filter(
      (r) =>
        (conf === "all" || r.conf === conf) &&
        (pop === "all" || String(r.pop) === pop)
    );
  }, [records, conf, pop]);

  const rows = useMemo(() => {
    const byMois = {};
    filtered.forEach((r) => {
      if (!byMois[r.mois]) byMois[r.mois] = [];
      byMois[r.mois].push(r.dens);
    });
    return Object.keys(byMois)
      .sort()
      .map((mois) => {
        const st = statsNormale(byMois[mois]);
        return {
          mois,
          n: byMois[mois].length,
          moyenne: st ? Number(st.moyenne.toFixed(4)) : null,
          valeurCarac: st ? Number(st.valeurCarac.toFixed(4)) : null,
          ks: st ? Number(st.ks.toFixed(3)) : null,
          s: st ? Number(st.sRetenu.toFixed(4)) : null,
        };
      });
  }, [filtered]);

  if (!active) return null;

  if (loading) {
    return <div style={{ color: COLORS.yellow, padding: 24 }}>Chargement masse volumique…</div>;
  }
  if (error) {
    return <div style={{ color: COLORS.red, padding: 24 }}>{error}</div>;
  }

  return (
    <div>
      <div style={S.header}>
        <div style={S.title}>Masse volumique — valeur caractéristique</div>
        <div style={S.subtitle}>
          Valeur caractéristique mensuelle (loi normale, EN 14358) · densités hors
          [0,7 – 2,0] exclues · {filtered.length} stack(s) pris en compte
        </div>
      </div>

      <div style={S.tabRow}>
        <span style={S.grpLabel}>Conformité</span>
        {CONF_KEYS.map((k) => (
          <button
            key={k.id}
            type="button"
            style={{ ...S.tab, ...(conf === k.id ? S.tabActive : {}) }}
            onClick={() => setConf(k.id)}
          >
            {k.label}
          </button>
        ))}
      </div>

      <div style={S.tabRow}>
        <span style={S.grpLabel}>Épaisseur</span>
        {POP_KEYS.map((k) => (
          <button
            key={k.id}
            type="button"
            style={{ ...S.tab, ...(pop === k.id ? S.tabActive : {}) }}
            onClick={() => setPop(k.id)}
          >
            {k.label}
          </button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div style={S.card}>
          <div style={{ color: COLORS.gray }}>Aucune donnée pour ces filtres.</div>
        </div>
      ) : (
        <>
          <div style={S.card}>
            <div style={S.cardTitle}>Masse volumique par mois</div>
            <ResponsiveContainer width="100%" height={340}>
              <ComposedChart data={rows} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                <XAxis dataKey="mois" tick={{ fill: COLORS.gray, fontSize: 11 }} angle={-35} textAnchor="end" height={60} />
                <YAxis
                  yAxisId="left"
                  tick={{ fill: COLORS.gray, fontSize: 11 }}
                  allowDecimals={false}
                  label={{ value: "Stacks", angle: -90, position: "insideLeft", fill: COLORS.gray, fontSize: 11 }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={["auto", "auto"]}
                  tick={{ fill: COLORS.carac, fontSize: 11 }}
                  label={{ value: "Densité", angle: 90, position: "insideRight", fill: COLORS.carac, fontSize: 11 }}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey="n"
                  name="Stacks"
                  fill={COLORS.bar}
                  stroke={COLORS.barEdge}
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="moyenne"
                  name="Moyenne"
                  stroke={COLORS.moy}
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: COLORS.moy }}
                  connectNulls={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="valeurCarac"
                  name="Valeur caractéristique"
                  stroke={COLORS.carac}
                  strokeWidth={2.5}
                  strokeDasharray="6 4"
                  dot={{ r: 4, fill: COLORS.carac }}
                  connectNulls={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div style={S.card}>
            <div style={S.cardTitle}>Récapitulatif mensuel</div>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {["Mois", "Valeur carac.", "Moyenne", "n", "kₛ", "s retenu"].map((h) => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.mois} style={S.tr}>
                      <td style={S.td}>{row.mois}</td>
                      <td style={{ ...S.td, color: COLORS.carac, fontWeight: 700 }}>
                        {row.valeurCarac != null ? row.valeurCarac : "N/A (n<2)"}
                      </td>
                      <td style={{ ...S.td, color: COLORS.moy }}>{row.moyenne ?? "N/A"}</td>
                      <td style={S.td}>{row.n}</td>
                      <td style={S.td}>{row.ks ?? "N/A"}</td>
                      <td style={S.td}>{row.s ?? "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const S = {
  header: { marginBottom: 20 },
  title: { fontSize: 20, fontWeight: 900, color: "#fff", letterSpacing: "0.04em" },
  subtitle: { fontSize: 12, color: COLORS.gray, marginTop: 4 },
  tabRow: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 },
  grpLabel: { fontSize: 12, color: COLORS.gray, marginRight: 4 },
  tab: {
    padding: "8px 16px",
    borderRadius: 8,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: COLORS.gray,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  tabActive: {
    borderColor: COLORS.yellow,
    color: COLORS.yellow,
    background: "rgba(230,255,62,0.08)",
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    marginBottom: 16,
    marginTop: 16,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.gray,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 16,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    padding: "10px 14px",
    textAlign: "left",
    color: COLORS.gray,
    fontWeight: 700,
    textTransform: "uppercase",
    fontSize: 10,
    letterSpacing: "0.06em",
    borderBottom: "1px solid #1e3a50",
  },
  tr: { borderBottom: "1px solid #1e3a50" },
  td: { padding: "10px 14px", color: "#e2e8f0" },
  tooltip: {
    background: "#071e2f",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "10px 14px",
  },
};
